/**
 * LabOrderPortal Component
 * 
 * Main patient-facing lab order portal accessed via PatientActionLink.
 * Shows order details, appointment booking, lab centers, and results.
 */

import React, { useState, useEffect } from 'react';
import {
  FlaskConical,
  Calendar,
  MapPin,
  FileText,
  Loader2,
  CheckCircle2,
  Clock,
  AlertCircle,
  Circle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import {
  getOrderDetails,
  PatientLabOrder,
} from '@/services/patientLabService';
import OrderDetailsTab from './OrderDetailsTab';
import AppointmentTab from './AppointmentTab';
import LabCentersTab from './LabCentersTab';
import ResultsTab from './ResultsTab';
import StatusTimeline from './StatusTimeline';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface LabOrderPortalProps {
  sessionToken: string;
  orderId: string;
  patientId: string;
  clinicName: string;
  patientName?: string;
}

const LabOrderPortal: React.FC<LabOrderPortalProps> = ({
  sessionToken,
  orderId,
  patientId,
  clinicName,
  patientName,
}) => {
  const [order, setOrder] = useState<PatientLabOrder | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('details');
  const { toast } = useToast();

  useEffect(() => {
    loadOrder();
  }, [orderId, sessionToken]);

  const loadOrder = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const orderData = await getOrderDetails(orderId, sessionToken);
      setOrder(orderData);
    } catch (err: any) {
      console.error('Error loading order:', err);
      setError(
        err.response?.data?.error?.message ||
          'Failed to load lab order details'
      );
      toast({
        title: 'Error',
        description: 'Failed to load lab order',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const refreshOrder = () => {
    loadOrder();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <Card className="border-slate-700 bg-slate-800 text-white">
            <CardHeader>
              <Skeleton className="h-8 w-48 bg-slate-700" />
              <Skeleton className="h-4 w-32 mt-2 bg-slate-700" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-64 w-full bg-slate-700" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-6 flex items-center justify-center">
        <Card className="max-w-md border-slate-700 bg-slate-800 text-white">
          <CardHeader>
            <div className="flex items-center gap-2 text-red-500">
              <AlertCircle className="h-5 w-5" />
              <CardTitle>Error Loading Order</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-slate-300">{error || 'Order not found'}</p>
            <Button
              onClick={loadOrder}
              className="mt-4"
              variant="outline"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasResults =
    order.status === 'completed' ||
    order.status === 'results_ready' ||
    order.status === 'reviewed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 text-center">
          <img
            src="/novamed_logo_white.svg"
            alt={clinicName}
            className="mx-auto h-8 mb-2"
          />
          <p className="text-slate-400 text-sm">{clinicName}</p>
        </div>

        <Card className="border-slate-700 bg-slate-800 text-white">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <FlaskConical className="h-5 w-5 text-blue-400" />
                  <CardTitle>Lab Order #{order.id.slice(0, 8)}</CardTitle>
                </div>
                {patientName && (
                  <p className="text-slate-400 text-sm">For {patientName}</p>
                )}
              </div>
              <StatusTimeline status={order.status} />
            </div>
          </CardHeader>

          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4 bg-slate-700">
                <TabsTrigger value="details">Order Details</TabsTrigger>
                <TabsTrigger value="appointment">Appointment</TabsTrigger>
                <TabsTrigger value="centers">Lab Centers</TabsTrigger>
                <TabsTrigger value="results" disabled={!hasResults}>
                  Results
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="mt-6">
                <OrderDetailsTab
                  order={order}
                  sessionToken={sessionToken}
                />
              </TabsContent>

              <TabsContent value="appointment" className="mt-6">
                <AppointmentTab
                  order={order}
                  sessionToken={sessionToken}
                  onBooked={refreshOrder}
                />
              </TabsContent>

              <TabsContent value="centers" className="mt-6">
                <LabCentersTab
                  order={order}
                  sessionToken={sessionToken}
                  onCenterSelected={refreshOrder}
                />
              </TabsContent>

              <TabsContent value="results" className="mt-6">
                <ResultsTab
                  orderId={orderId}
                  sessionToken={sessionToken}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LabOrderPortal;
